import { memo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Phone as PhoneIcon, EnvelopeSimple, VideoCamera, NotePencil, ChatCircleDots } from '@phosphor-icons/react';
import { DatePicker, TimePicker } from './DateTimePickers';

const ACTIVITY_ICONS = { call: PhoneIcon, email: EnvelopeSimple, meeting: VideoCamera, note: NotePencil, sms: ChatCircleDots };

// ACT!-style call outcomes — the seven things that actually happen when you
// pick up the phone. Keeping this list SHORT is intentional: a longer list
// means users don't pick one, and unused fields are worse than no fields.
const CALL_OUTCOMES = [
  'Connected',
  'Left voicemail',
  'No answer',
  'Callback requested',
  'Meeting scheduled',
  'Not interested',
  'Bad number',
];

/**
 * ActivityDialog — extracted from ContactCardPage so the parent doesn't re-render
 * its 90-line JSX every time you type in an unrelated form field.
 *
 * Wrapped in memo so it only re-renders when its own props change.
 */
function ActivityDialogInner({ open, onOpenChange, activityForm, setActivityForm, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] rounded-none border-[#999] p-0 gap-0" data-testid="activity-dialog">
        <DialogHeader className="px-4 pt-3 pb-2 bg-[#E0E0E0] border-b border-[#999]">
          <DialogTitle className="text-[13px] font-bold text-[#333]">Log Activity</DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div className="flex gap-1 flex-wrap">
            {['call', 'sms', 'email', 'meeting', 'note'].map((type) => {
              const Icon = ACTIVITY_ICONS[type];
              const labels = { call: 'Call', sms: 'Text', email: 'Email', meeting: 'Meeting', note: 'Note' };
              return (
                <button
                  key={type}
                  onClick={() => setActivityForm((f) => ({
                    ...f,
                    activity_type: type,
                    // Sensible defaults per type — keeps the form clean.
                    direction: type === 'sms' ? (f.direction || 'sent') : '',
                    outcome: type === 'call' ? f.outcome : '',
                  }))}
                  className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium border transition-colors ${activityForm.activity_type === type ? 'bg-[#0B5394] text-white border-[#0B5394]' : 'border-[#B0B0B0] text-[#555] hover:bg-[#E8E8E8]'}`}
                  data-testid={`act-type-${type}`}
                >
                  <Icon size={12} weight="bold" />
                  <span>{labels[type]}</span>
                </button>
              );
            })}
          </div>
          <input
            autoFocus
            value={activityForm.title}
            onChange={(e) => setActivityForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="What's this about?"
            className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]"
            data-testid="act-title"
          />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] font-medium text-[#666]">Date</label>
              <DatePicker
                value={activityForm.scheduled_date}
                onChange={(v) => setActivityForm((f) => ({ ...f, scheduled_date: v }))}
                testId="act-date"
              />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#666]">Time</label>
              <TimePicker
                value={activityForm.scheduled_time}
                onChange={(v) => setActivityForm((f) => ({ ...f, scheduled_time: v }))}
                testId="act-time"
              />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#666]">Priority</label>
              <select
                value={activityForm.priority}
                onChange={(e) => setActivityForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full h-[26px] px-1 text-[11px] border border-[#B0B0B0] mt-0.5 bg-white"
                data-testid="act-priority"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          {/* Call outcome — ACT!-style. Only visible when logging a call.
              A call without an outcome is useless data; a call WITH an
              outcome is a reportable event ("who did we leave voicemail
              for last week?" etc). Kept short to encourage picking one. */}
          {activityForm.activity_type === 'call' && (
            <div data-testid="act-call-outcome-block">
              <label className="text-[10px] font-medium text-[#666]">Outcome</label>
              <select
                value={activityForm.outcome || ''}
                onChange={(e) => setActivityForm((f) => ({ ...f, outcome: e.target.value }))}
                className="w-full h-[26px] px-1 text-[11px] border border-[#B0B0B0] mt-0.5 bg-white"
                data-testid="act-outcome"
              >
                <option value="">— How did the call go? —</option>
                {CALL_OUTCOMES.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          )}
          {/* SMS direction — did they text me, or did I text them? Two
              buttons is faster than a dropdown for a binary. */}
          {activityForm.activity_type === 'sms' && (
            <div data-testid="act-sms-direction-block">
              <label className="text-[10px] font-medium text-[#666] block mb-1">Direction</label>
              <div className="flex gap-1">
                {[
                  { v: 'sent', label: 'I texted them' },
                  { v: 'received', label: 'They texted me' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setActivityForm((f) => ({ ...f, direction: opt.v }))}
                    className={`flex-1 px-2 py-1 text-[11px] font-medium border transition-colors ${
                      (activityForm.direction || 'sent') === opt.v
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'border-[#B0B0B0] text-[#555] hover:bg-[#E8E8E8]'
                    }`}
                    data-testid={`act-direction-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {activityForm.activity_type !== 'note' && (
            <div>
              <label className="text-[10px] font-medium text-[#666]">Reminder</label>
              <select
                value={activityForm.reminder_minutes || 0}
                onChange={(e) => setActivityForm((f) => ({ ...f, reminder_minutes: parseInt(e.target.value, 10) || 0 }))}
                className="w-full h-[26px] px-1 text-[11px] border border-[#B0B0B0] mt-0.5 bg-white"
                data-testid="act-reminder"
              >
                <option value="0">No reminder</option>
                <option value="5">5 minutes before</option>
                <option value="15">15 minutes before</option>
                <option value="30">30 minutes before</option>
                <option value="60">1 hour before</option>
                <option value="120">2 hours before</option>
                <option value="1440">1 day before</option>
              </select>
            </div>
          )}
          <textarea
            value={activityForm.content}
            onChange={(e) => setActivityForm((f) => ({ ...f, content: e.target.value }))}
            rows={2}
            className="w-full px-2 py-1 text-[12px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394] resize-none"
            placeholder="Details..."
            data-testid="act-content"
          />
          <div className="border-t border-[#DDD] pt-2">
            <label className="flex items-center gap-1.5 text-[11px] text-[#333] font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={activityForm.recurring}
                onChange={(e) => setActivityForm((f) => ({ ...f, recurring: e.target.checked }))}
                data-testid="act-recurring"
              />
              Make recurring
            </label>
            {activityForm.recurring && (
              <div className="grid grid-cols-2 gap-2 mt-1.5 pl-5">
                <div>
                  <label className="text-[10px] font-medium text-[#666]">Repeat</label>
                  <select
                    value={activityForm.recurrence}
                    onChange={(e) => setActivityForm((f) => ({ ...f, recurrence: e.target.value }))}
                    className="w-full h-[26px] px-1 text-[11px] border border-[#B0B0B0] mt-0.5"
                    data-testid="act-recurrence-pattern"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 weeks</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[#666]">Occurrences</label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={activityForm.occurrences}
                    onChange={(e) => setActivityForm((f) => ({ ...f, occurrences: parseInt(e.target.value) || 1 }))}
                    className="w-full h-[26px] px-1 text-[11px] border border-[#B0B0B0] mt-0.5"
                    data-testid="act-occurrences"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-2 bg-[#E0E0E0] border-t border-[#999]">
          <button
            onClick={() => onOpenChange(false)}
            className="px-3 py-1 text-[11px] font-medium border border-[#999] bg-white hover:bg-[#F5F5F5] text-[#333]"
            data-testid="cancel-activity-btn"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-1 text-[11px] font-bold bg-[#0B5394] text-white hover:bg-[#073763]"
            data-testid="save-activity-btn"
          >
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default memo(ActivityDialogInner);
